import { Text } from "@mantine/core";
import { DateTime } from "luxon";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import {
  MantineNullCell,
  renderNullableCell
} from "../../../../../mantine/mrt/components/cell/MantineNullCell";
import { getDateColumn } from "../../../../../mantine/mrt/components/columns/getDateColumn";
import { HeaderWithHelpIcon } from "../../../../../mantine/mrt/components/header/HeaderWithHelpIcon";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { DATE_DISPLAY_FORMAT } from "../../../../../utils/dates/dates.constants";
import { roundedNumericFormatter } from "../../../../../utils/RoundedNumberFormat";
import { getEegStundenregelLabel } from "../../../../EEGAnlage/utils/getEegStundenregelLabel";
import type { GeneratorListItemResponse } from "../../../../ObjectModals/GeneratorModal/GeneratorModal.types";
import { getFoerderregimeLabel } from "../../../../Paragraph6/utils/getFoerderregimeLabel";
import { getVeraeusserungsformLabel } from "../../../../PeriodBasedComponents/MarketingTypeTable/MarketingTypeTable.constants";
import { AnzulegenderWertCell } from "./AnzulegenderWertCell";

const AZW_HELP_TEXT =
  "Anzulegender Wert / Einspeisevergütung zeigt den aktuellen Wert. Diese Eigenschaft ist zeitlich veränderbar und als Zeitreihe hinterlegt; frühere und zukünftige Werte können im Bearbeitungsmodus eingesehen werden. Bei mehreren hinterlegten Werten wird neben dem aktuellen Wert ein Symbol angezeigt, das zur Tabelle mit allen anzulegenden Werten führt.";

interface GetEegAnlageColumnsProps {
  projectId: string;
  siteId: number;
}

export function getEegAnlageColumns({
  projectId,
  siteId
}: GetEegAnlageColumnsProps): Array<MRT_ColumnDef<GeneratorListItemResponse>> {
  return [
    {
      header: "Anlagenschlüssel",
      accessorKey: "anlagenschluessel_eeg",
      Cell: renderNullableCell,
      enableCellHoverReveal: true
    },
    getDateColumn<GeneratorListItemResponse>({
      key: "eeg_inbetriebnahmedatum",
      options: {
        header: "EEG-Inbetriebnahme"
      },
      renderUndefinedAs: <MantineNullCell />
    }),
    {
      id: "anzulegender_wert",
      header: "Anzulegender Wert / Einspeisevergütung",
      Header: (
        <HeaderWithHelpIcon
          headerText="Anzulegender Wert / Einspeisevergütung"
          helpText={AZW_HELP_TEXT}
        />
      ),
      size: 250,
      accessorFn: (row) => {
        const value = row.anzulegender_wert;
        return value ? `${roundedNumericFormatter(value)} ct/kWh` : null;
      },
      Cell: ({ cell, row }) => (
        <AnzulegenderWertCell
          count={row.original.count_anzulegender_werte ?? 0}
          formattedValue={cell.getValue<string | null>()}
          generatorId={row.original.id}
          projectId={projectId}
          siteId={siteId}
        />
      )
    },
    {
      id: "foerderregime",
      header: "Förderregime",
      accessorFn: (row) =>
        row.foerderregime ? getFoerderregimeLabel(row.foerderregime) : null,
      Cell: renderNullableCell,
      enableCellHoverReveal: true
    },
    {
      id: "gebotstermin",
      header: "Gebotstermin",
      accessorFn: (rowData: GeneratorListItemResponse) => {
        if (rowData.gebotstermin) {
          return backendDateOrDateTimeToLuxonDateTime(
            rowData.gebotstermin
          ).toJSDate();
        }
        if (rowData.jahr_des_gebotstermins) {
          return DateTime.fromObject({
            year: rowData.jahr_des_gebotstermins,
            month: 1,
            day: 1
          }).toJSDate();
        }
        return null;
      },
      Cell: ({ row, renderedCellValue }) => {
        if (renderedCellValue == null || !(renderedCellValue instanceof Date)) {
          return <MantineNullCell />;
        }

        if (row.original.gebotstermin) {
          return DateTime.fromJSDate(renderedCellValue).toFormat(
            DATE_DISPLAY_FORMAT
          );
        } else {
          return DateTime.fromJSDate(renderedCellValue).year;
        }
      }
    },
    {
      id: "veraeusserungsform",
      header: "Veräußerungsform",
      accessorFn: (row) =>
        row.veraeusserungsform
          ? getVeraeusserungsformLabel(row.veraeusserungsform)
          : null,
      Cell: renderNullableCell
    },
    {
      id: "eeg_und_p51",
      header: "EEG und § 51 EEG - Negative Stunden Regelung",
      size: 280,
      accessorFn: (row) =>
        row.p51_rule_is_relevant
          ? getEegStundenregelLabel(row.eeg_version, row.applied_p51_rule)
          : null,
      Cell: renderNullableCell,
      enableCellHoverReveal: true
    }
  ];
}
